import { Box, Button, Flex, Grid, GridItem, Text } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Select, Input, ButtonIcon, FormFeedback } from '@morressier/alembic';
import { faPlus, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useState } from 'react';
import AutoSuggest, { escapeLuceneSpecialChars } from './AutoSuggest';
import MultiSelectAutosuggest from './MultiSelectAutosuggest';
import { CustomSearchSelect } from 'src/components/UserRegistrationSteps/StepThree/CustomSearchSelect';
import {
  IReviewerSearchPropertyResponse,
  ISearchCriteria
} from 'src/api/queries';
import { ILexiconField } from '@publishing-engine/domain/models';

type ReviewerSearchFieldValue = string | string[];

interface AdvancedSearchForm {
  searchFields: {
    id: string;
    value: ReviewerSearchFieldValue;
  }[];
}

interface GlobalSearchForm {
  id: string;
  value: string;
}

const AVERAGE_R_SCORE_LABEL = 'AverageRScore';
const MULTI_SELECT_HELPER_DISPLAY: 'text' | 'tooltip' = 'tooltip';

const AVERAGE_R_SCORE_RANGES: Record<string, [string, string]> = {
  Low: ['1.0', '1.66'],
  Moderate: ['1.67', '2.33'],
  High: ['2.34', '3.0']
};

interface AdvancedSearchProps {
  advancedSearchFields: any[];
  reviewerProperties: any;
  reviewerConfig: any[] | undefined;
  submissionContextId: string;
  lexiconProjectId: string;
  isFetching: boolean;
  onSearch: (
    advancedCriteria: ISearchCriteria[],
    globalCriteria: ISearchCriteria[]
  ) => void;
  onReset: () => void;
  globalAdvancedSearchFields: ILexiconField[];
  globalReviewerProperties:
    | IReviewerSearchPropertyResponse[]
    | null
    | undefined;
  globalLexiconConfig: boolean | undefined;
}

const AdvancedSearch = ({
  advancedSearchFields,
  reviewerProperties,
  reviewerConfig,
  submissionContextId,
  lexiconProjectId,
  isFetching,
  onSearch,
  onReset,
  globalAdvancedSearchFields,
  globalReviewerProperties,
  globalLexiconConfig
}: AdvancedSearchProps) => {
  const methods = useForm<AdvancedSearchForm>({
    defaultValues: {
      searchFields: [{ id: '', value: '' }]
    },
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  const globalMethods = useForm<GlobalSearchForm>({
    defaultValues: { id: '', value: '' },
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    clearErrors,
    formState: { errors }
  } = methods;

  const {
    control: globalControl,
    handleSubmit: globalHandleSubmit,
    watch: globalWatch,
    clearErrors: globalClearErrors,
    formState: { errors: globalErrors }
  } = globalMethods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'searchFields'
  });

  const isMaxFieldsReached = fields.length >= 5;

  const [fieldsWithDuplicates, setFieldsWithDuplicates] = useState<Set<number>>(
    new Set()
  );
  const hasDuplicateError = fieldsWithDuplicates.size > 0;

  const [submitted, setSubmitted] = useState(false);

  const getPropertyByPredicateLabel = (predicateLabel?: string) =>
    reviewerProperties?.[0]?.properties.find(
      (p: any) => p.predicateLabel === predicateLabel
    );

  const getAdvancedSearchConfig = (predicateLabel?: string): any => {
    if (!predicateLabel) return undefined;

    const lexiconField = advancedSearchFields.find(
      f => f.predicateLabel === predicateLabel
    );

    if (!lexiconField || lexiconField.advancedSearch === false) {
      return undefined;
    }

    return lexiconField.advancedSearch;
  };

  const isMultiSelectField = (predicateLabel?: string) =>
    !!getAdvancedSearchConfig(predicateLabel)?.multiSelect &&
    !!getAdvancedSearchConfig(predicateLabel)?.autosuggest;

  const getDefaultValueByPredicateLabel = (
    predicateLabel?: string
  ): ReviewerSearchFieldValue => (isMultiSelectField(predicateLabel) ? [] : '');

  const normalizeSearchFieldValue = (
    value: ReviewerSearchFieldValue
  ): string[] => {
    if (Array.isArray(value)) {
      return value.map(item => item.trim()).filter(Boolean);
    }

    const trimmedValue = value?.trim();
    return trimmedValue ? [trimmedValue] : [];
  };

  const isNumericType = (datatype?: string) =>
    datatype === 'xsd:integer' || datatype === 'xsd:decimal';

  const isDateType = (datatype?: string) => datatype === 'xsd:dateTime';

  const getIsAutosuggestEnabled = (predicateLabel: string): boolean => {
    return !!getAdvancedSearchConfig(predicateLabel)?.autosuggest;
  };

  const getValueRules = (
    predicateLabel?: string,
    datatype?: string
  ): {
    required?: string;
    validate?: (value: ReviewerSearchFieldValue) => true | string;
  } => {
    if (predicateLabel === AVERAGE_R_SCORE_LABEL) {
      return { required: 'Please select value' };
    }

    if (isDateType(datatype)) {
      return { required: 'Please select date' };
    }

    if (isNumericType(datatype)) {
      return {
        validate: value => {
          if (typeof value !== 'string' || value.trim() === '') {
            return 'Please enter number';
          }
          if (Number(value) < 0 || Object.is(value, -0)) {
            return 'Value must be 0 or greater';
          }

          return true;
        }
      };
    }

    return {
      validate: value =>
        normalizeSearchFieldValue(value).length > 0 || 'Please enter value'
    };
  };

  const buildSearchCriteria = (data: AdvancedSearchForm): ISearchCriteria[] => {
    return data.searchFields
      .filter(field => normalizeSearchFieldValue(field.value).length > 0)
      .map(field => {
        const property = reviewerProperties?.[0]?.properties?.find(
          ({ predicateLabel }: any) => predicateLabel === field.id
        );
        const normalizedValue = normalizeSearchFieldValue(field.value);
        const datatype = property?.datatype ?? 'xsd:string';

        if (field.id === AVERAGE_R_SCORE_LABEL) {
          return {
            id: property?.id ?? field.id,
            value:
              typeof field.value === 'string' ? field.value.split(',') : [],
            operator: 'AND' as const,
            datatype: 'xsd:decimal'
          };
        }

        const escapedValue =
          datatype === 'xsd:dateTime'
            ? normalizedValue
            : normalizedValue.map(escapeLuceneSpecialChars);

        return {
          id: property?.id ?? field.id,
          value: escapedValue,
          operator: (normalizedValue.length > 1 ? 'OR' : 'AND') as 'OR' | 'AND',
          datatype
        };
      });
  };

  const globalPropertyId = globalWatch('id');
  const globalProperty = globalReviewerProperties?.[0]?.properties.find(
    p => p.predicateLabel === globalPropertyId
  );

  const getGlobalAdvancedSearchConfig = (predicateLabel?: string) => {
    if (!predicateLabel) return undefined;

    const lexiconField = globalAdvancedSearchFields.find(
      f => f.predicateLabel === predicateLabel
    );

    if (!lexiconField || lexiconField.advancedSearch === false) {
      return undefined;
    }

    return lexiconField.advancedSearch;
  };

  const isGlobalAutosuggestEnabled =
    !!getGlobalAdvancedSearchConfig(globalPropertyId)?.autosuggest;
  const isGlobalDateType = isDateType(globalProperty?.datatype);
  const isGlobalNumericType = isNumericType(globalProperty?.datatype);

  const getGlobalValueRules = (): {
    required?: string;
    validate?: (value: string) => true | string;
  } => {
    if (globalPropertyId === AVERAGE_R_SCORE_LABEL) {
      return { required: 'Please select value' };
    }

    if (isGlobalDateType) {
      return { required: 'Please select date' };
    }

    if (isGlobalNumericType) {
      return {
        validate: value =>
          typeof value === 'string' && value.trim() !== ''
            ? true
            : 'Please enter number'
      };
    }

    return {
      validate: value =>
        (value && value.trim().length > 0) || 'Please enter value'
    };
  };

  const buildGlobalSearchCriteria = (
    data: GlobalSearchForm
  ): ISearchCriteria[] => {
    const trimmedValue = data.value?.trim() ?? '';
    if (!trimmedValue) return [];

    const property = globalReviewerProperties?.[0]?.properties.find(
      p => p.predicateLabel === data.id
    );
    const datatype = property?.datatype ?? 'xsd:string';

    if (data.id === AVERAGE_R_SCORE_LABEL) {
      return [
        {
          id: property?.id ?? data.id,
          value: data.value.split(','),
          operator: 'AND' as const,
          datatype: 'xsd:decimal'
        }
      ];
    }

    const escapedValue =
      datatype === 'xsd:dateTime'
        ? [trimmedValue]
        : [escapeLuceneSpecialChars(trimmedValue)];

    return [
      {
        id: property?.id ?? data.id,
        value: escapedValue,
        operator: 'AND' as const,
        datatype
      }
    ];
  };

  const watchedSearchFields = watch('searchFields');
  const watchedGlobalId = globalWatch('id');
  const watchedGlobalValue = globalWatch('value');

  const hasAnyInput =
    watchedSearchFields.some(
      f => !!f.id || normalizeSearchFieldValue(f.value).length > 0
    ) ||
    !!watchedGlobalId ||
    (watchedGlobalValue?.trim() ?? '').length > 0;

  const showNoCriteriaError = submitted && !hasAnyInput;

  const handleCombinedSubmit = async () => {
    if (hasDuplicateError) return;

    const rawAdvancedData = methods.getValues();
    const rawGlobalData = globalMethods.getValues();

    const hasAdvancedInput = rawAdvancedData.searchFields.some(
      f => !!f.id || normalizeSearchFieldValue(f.value).length > 0
    );
    const hasGlobalInput =
      !!rawGlobalData.id || (rawGlobalData.value?.trim() ?? '').length > 0;

    if (!hasAdvancedInput && !hasGlobalInput) {
      setSubmitted(true);
      return;
    }

    setSubmitted(false);

    let advancedData = rawAdvancedData;
    let globalData = rawGlobalData;
    let advancedValid = !hasAdvancedInput;
    let globalValid = !hasGlobalInput;

    if (hasAdvancedInput) {
      await handleSubmit(data => {
        advancedData = data;
        advancedValid = true;
      })();
    }

    if (hasGlobalInput) {
      await globalHandleSubmit(data => {
        globalData = data;
        globalValid = true;
      })();
    }

    if (!advancedValid || !globalValid) return;

    onSearch(
      buildSearchCriteria(advancedData),
      buildGlobalSearchCriteria(globalData)
    );
  };

  const handleCombinedReset = () => {
    reset({
      searchFields: [{ id: '', value: '' }]
    });
    globalMethods.reset({ id: '', value: '' });
    setFieldsWithDuplicates(new Set());
    setSubmitted(false);
    onReset();
  };

  return (
    <Flex direction="column" gap={2} backgroundColor="surfacePrimaryFill">
      {fields.map((field, index) => {
        const propertyId = watch(`searchFields.${index}.id`);
        const property = getPropertyByPredicateLabel(propertyId);
        return (
          <Box key={field.id}>
            <Grid
              templateColumns="30% 1fr auto auto"
              columnGap={4}
              rowGap={2}
              alignItems="flex-start"
            >
              <GridItem colSpan={1}>
                <Controller
                  control={control}
                  name={`searchFields.${index}.id`}
                  rules={{
                    required: 'Please select search criteria'
                  }}
                  render={({ field, fieldState }) => (
                    <CustomSearchSelect
                      value={field.value}
                      onChange={(val: string) => {
                        if (val === field.value) {
                          return;
                        }

                        field.onChange(val);
                        methods.setValue(
                          `searchFields.${index}.value`,
                          getDefaultValueByPredicateLabel(val),
                          {
                            shouldValidate: false
                          }
                        );
                        clearErrors(`searchFields.${index}.value`);

                        if (fieldsWithDuplicates.size > 0) {
                          setFieldsWithDuplicates(new Set());
                        }
                      }}
                      options={advancedSearchFields.map(f => ({
                        value: f.predicateLabel,
                        label: f.label
                      }))}
                      placeholder="criteria"
                      isInvalid={!!fieldState.error}
                    />
                  )}
                />
              </GridItem>

              {/* Input OR Autosuggest */}
              <GridItem colSpan={1}>
                <Controller
                  key={`value-${index}-${propertyId || 'empty'}`}
                  shouldUnregister
                  control={control}
                  name={`searchFields.${index}.value`}
                  defaultValue={getDefaultValueByPredicateLabel(propertyId)}
                  rules={getValueRules(
                    property?.predicateLabel,
                    property?.datatype
                  )}
                  render={({ field, fieldState }) => {
                    if (property?.predicateLabel === AVERAGE_R_SCORE_LABEL) {
                      return (
                        <Select
                          value={
                            typeof field.value === 'string' ? field.value : ''
                          }
                          onChange={event => field.onChange(event.target.value)}
                        >
                          <option value="" disabled>
                            Select range
                          </option>
                          {Object.keys(AVERAGE_R_SCORE_RANGES).map(range => (
                            <option
                              key={range}
                              value={AVERAGE_R_SCORE_RANGES[range]?.toString()}
                            >
                              {range}
                            </option>
                          ))}
                        </Select>
                      );
                    }

                    if (isDateType(property?.datatype)) {
                      return (
                        <Input
                          type="date"
                          name="date"
                          max={new Date().toISOString().split('T')[0]}
                          value={
                            typeof field.value === 'string' ? field.value : ''
                          }
                          onChange={event => field.onChange(event.target.value)}
                        />
                      );
                    }

                    if (isNumericType(property?.datatype)) {
                      return (
                        <Input
                          type="number"
                          name="number"
                          placeholder="Enter numeric values Ex: 1, 2"
                          step={
                            property?.datatype === 'xsd:decimal' ? 'any' : 1
                          }
                          value={
                            typeof field.value === 'string' ? field.value : ''
                          }
                          min={3}
                          onChange={event => field.onChange(event.target.value)}
                        />
                      );
                    }

                    if (getIsAutosuggestEnabled(propertyId)) {
                      return isMultiSelectField(propertyId) ? (
                        <MultiSelectAutosuggest
                          key={propertyId}
                          value={Array.isArray(field.value) ? field.value : []}
                          onChange={val => {
                            field.onChange(val);
                            clearErrors(`searchFields.${index}.value`);
                          }}
                          ctxId={submissionContextId}
                          projectId={lexiconProjectId}
                          propertyId={property?.id ?? ''}
                          fieldLabel={
                            reviewerConfig?.filter(
                              config =>
                                config.predicateLabel ===
                                property?.predicateLabel
                            )[0]?.label
                          }
                          isInvalid={!!fieldState.error}
                          helperDisplay={MULTI_SELECT_HELPER_DISPLAY}
                          onDuplicateError={hasDuplicate =>
                            setFieldsWithDuplicates(prev => {
                              const alreadyPresent = prev.has(index);

                              if (alreadyPresent === hasDuplicate) {
                                return prev;
                              }

                              const next = new Set(prev);

                              if (hasDuplicate) {
                                next.add(index);
                              } else {
                                next.delete(index);
                              }

                              return next;
                            })
                          }
                        />
                      ) : (
                        <AutoSuggest
                          key={propertyId}
                          value={
                            typeof field.value === 'string' ? field.value : ''
                          }
                          onChange={val => {
                            field.onChange(val);
                            clearErrors(`searchFields.${index}.value`);
                          }}
                          ctxId={submissionContextId}
                          projectId={lexiconProjectId}
                          propertyId={property?.id ?? ''}
                          isInvalid={!!fieldState.error}
                        />
                      );
                    }

                    return (
                      <Input
                        autoComplete="off"
                        name="search"
                        placeholder="Enter value for selected criterion (e.g., name, email, institution, etc.)"
                        value={
                          typeof field.value === 'string' ? field.value : ''
                        }
                        onChange={event => field.onChange(event.target.value)}
                      />
                    );
                  }}
                />
              </GridItem>

              {/* Add Button */}
              <GridItem>
                <ButtonIcon
                  ariaLabel="Add Field"
                  icon={<FontAwesomeIcon icon={faPlus} />}
                  onClick={() =>
                    !isMaxFieldsReached &&
                    append({
                      id: '',
                      value: ''
                    })
                  }
                  disabled={isMaxFieldsReached}
                />
              </GridItem>

              {/* Delete Button */}
              <GridItem>
                <ButtonIcon
                  ariaLabel="Delete Field"
                  icon={<FontAwesomeIcon icon={faTrashAlt} />}
                  onClick={() => remove(index)}
                  isDisabled={fields.length === 1}
                />
              </GridItem>

              {/* Error under dropdown */}
              <GridItem colSpan={1}>
                {errors?.searchFields?.[index]?.id && (
                  <FormFeedback variant="error">
                    {errors.searchFields[index].id?.message}
                  </FormFeedback>
                )}
              </GridItem>

              {/* Error under input */}
              <GridItem colSpan={1}>
                {errors?.searchFields?.[index]?.value && (
                  <FormFeedback variant="error">
                    {errors.searchFields[index].value?.message}
                  </FormFeedback>
                )}
              </GridItem>
            </Grid>
          </Box>
        );
      })}

      {globalLexiconConfig && globalAdvancedSearchFields?.length > 0 && (
        <Box borderTop="1px solid" borderColor="neutralPrimaryBorder" pt={2}>
          <Text textStyle="headingItem" mb={1}>
            Global Reviewer Search
          </Text>
          <Grid
            templateColumns="30% 1fr"
            columnGap={4}
            rowGap={2}
            alignItems="flex-start"
          >
            <GridItem colSpan={1}>
              <Controller
                control={globalControl}
                name="id"
                rules={{ required: 'Please select search criteria' }}
                render={({ field, fieldState }) => (
                  <CustomSearchSelect
                    value={field.value}
                    onChange={(val: string) => {
                      if (val === field.value) return;

                      field.onChange(val);
                      globalMethods.setValue('value', '', {
                        shouldValidate: false
                      });
                      globalClearErrors('value');
                    }}
                    options={globalAdvancedSearchFields.map(f => ({
                      value: f.predicateLabel,
                      label: f.label
                    }))}
                    placeholder="criteria"
                    isInvalid={!!fieldState.error}
                  />
                )}
              />
            </GridItem>

            <GridItem colSpan={1}>
              <Controller
                key={`global-value-${globalPropertyId || 'empty'}`}
                shouldUnregister
                control={globalControl}
                name="value"
                defaultValue=""
                rules={getGlobalValueRules()}
                render={({ field, fieldState }) => {
                  if (
                    globalProperty?.predicateLabel === AVERAGE_R_SCORE_LABEL
                  ) {
                    return (
                      <Select
                        value={field.value}
                        onChange={event => field.onChange(event.target.value)}
                      >
                        <option value="" disabled>
                          Select range
                        </option>
                        {Object.keys(AVERAGE_R_SCORE_RANGES).map(range => (
                          <option
                            key={range}
                            value={AVERAGE_R_SCORE_RANGES[range]?.toString()}
                          >
                            {range}
                          </option>
                        ))}
                      </Select>
                    );
                  }

                  if (isGlobalDateType) {
                    return (
                      <Input
                        type="date"
                        name="date"
                        max={new Date().toISOString().split('T')[0]}
                        value={field.value}
                        onChange={event => field.onChange(event.target.value)}
                      />
                    );
                  }

                  if (isGlobalNumericType) {
                    return (
                      <Input
                        type="number"
                        name="number"
                        placeholder="Enter numeric values Ex: 1, 2.0"
                        step={
                          globalProperty?.datatype === 'xsd:decimal' ? 'any' : 1
                        }
                        value={field.value}
                        onChange={event => field.onChange(event.target.value)}
                      />
                    );
                  }

                  if (isGlobalAutosuggestEnabled) {
                    return (
                      <AutoSuggest
                        key={globalPropertyId}
                        value={field.value}
                        onChange={val => {
                          field.onChange(val);
                          globalClearErrors('value');
                        }}
                        ctxId={submissionContextId}
                        projectId={lexiconProjectId}
                        propertyId={globalProperty?.id ?? ''}
                        isInvalid={!!fieldState.error}
                      />
                    );
                  }

                  return (
                    <Input
                      autoComplete="off"
                      name="search"
                      placeholder="Enter value"
                      value={field.value}
                      onChange={event => field.onChange(event.target.value)}
                    />
                  );
                }}
              />
            </GridItem>

            <GridItem colSpan={1}>
              {globalErrors?.id && (
                <FormFeedback variant="error">
                  {globalErrors.id.message}
                </FormFeedback>
              )}
            </GridItem>

            <GridItem colSpan={1}>
              {globalErrors?.value && (
                <FormFeedback variant="error">
                  {globalErrors.value.message}
                </FormFeedback>
              )}
            </GridItem>
          </Grid>
        </Box>
      )}

      {showNoCriteriaError && (
        <FormFeedback variant="error">
          Please select at least one search criteria
        </FormFeedback>
      )}

      <Flex justifyContent="flex-end" gap={2} mt={1}>
        <Button variant="secondary" onClick={() => handleCombinedReset()}>
          Reset
        </Button>
        <Button
          variant="primary"
          type="button"
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            handleCombinedSubmit();
          }}
          isLoading={isFetching}
          loadingText="Loading..."
        >
          Search
        </Button>
      </Flex>
    </Flex>
  );
};

export default AdvancedSearch;
